"use client";

import React, { useContext, useEffect, useState } from "react";
import PageMeta from "../../components/common/PageMeta";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import ClientList, { ClienteB2CExtended } from "./ClientList";
import AgencyList, { AgenciaB2B } from "./AgencyList";
import AnalisisClientes from "./AnalisisClientes";
import useApi, { ClienteB2C, ReservaB2C } from "../../hooks/useApi";
import AuthContext from "../../context/AuthContext";
import { toast } from "react-hot-toast";

const Clientes: React.FC = () => {
  const { user, isLoading: authLoading } = useContext(AuthContext);
  const {
    getClients,
    createClient,
    updateClient,
    deleteClient,
    getAgencies,
    createAgency,
    updateAgency,
    deleteAgency,
    fetchAnalysisMetrics,
    getClientById,
    getClientHistory,
    getAgencyById,
    getAgencyReservations,
    getAnalytics,
    getClientPayments,
    getAgencyPayments
  } = useApi();

  const [activeTab, setActiveTab] = useState("b2c");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [clients, setClients] = useState<ClienteB2C[]>([]);
  const [agencies, setAgencies] = useState<AgenciaB2B[]>([]);
  const [analysisData, setAnalysisData] = useState({
    mostPaid: "Cargando...",
    leastPaid: "Cargando...",
    topAgencyMetric: "Cargando..."
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch all data in parallel to reduce cascade effects
        const [clientsData, agenciesData] = await Promise.all([
          getClients() as Promise<ClienteB2C[]>,
          getAgencies() as Promise<AgenciaB2B[]>
        ]);
        
        // Set initial data without additional API calls
        setClients(clientsData.map(client => ({
          ...client,
          reservations: client.reservations || 0
        })));
        
        setAgencies(agenciesData.map(agency => ({
          ...agency,
          reservations: agency.reservations || 0
        })));
        
        // Only if needed, fetch additional details in batches (consider removing if not critical)
        if (clientsData.length > 0) {
          // Using setTimeout to delay this secondary batch of requests
          setTimeout(async () => {
            try {
              // Process in smaller batches to avoid too many parallel requests
              const batchSize = 3;
              for (let i = 0; i < clientsData.length; i += batchSize) {
                const batch = clientsData.slice(i, i + batchSize);
                
                const updatedBatch = await Promise.all(
                  batch.map(async (client: ClienteB2C) => {
                    const history = await getClientHistory(client.id);
                    return { ...client, reservations: history.length };
                  })
                );
                
                // Update clients state with the processed batch
                setClients(prev => {
                  const updated = [...prev];
                  updatedBatch.forEach(client => {
                    const index = updated.findIndex(c => c.id === client.id);
                    if (index !== -1) {
                      updated[index] = client;
                    }
                  });
                  return updated;
                });
              }
            } catch (error) {
              toast.error("Error updating client details");
            }
          }, 500);
        }
        
        // Same approach for agencies, but only if needed
        if (agenciesData.length > 0) {
          setTimeout(async () => {
            try {
              const batchSize = 3;
              for (let i = 0; i < agenciesData.length; i += batchSize) {
                const batch = agenciesData.slice(i, i + batchSize);
                
                const updatedBatch = await Promise.all(
                  batch.map(async (agency: AgenciaB2B) => {
                    const reservations = await getAgencyReservations(agency.id);
                    return { ...agency, reservations: reservations.length };
                  })
                );
                
                setAgencies(prev => {
                  const updated = [...prev];
                  updatedBatch.forEach(agency => {
                    const index = updated.findIndex(a => a.id === agency.id);
                    if (index !== -1) {
                      updated[index] = agency;
                    }
                  });
                  return updated;
                });
              }
            } catch (error) {
              toast.error("Error updating agency details");
            }
          }, 1000);
        }

        // Test payment API - debugging only
        if (getClientPayments) {
          try {
            console.log("Testing payment API...");
            const testPayments = await getClientPayments(1); // Test with ID 1
            console.log("Test payments result:", testPayments);
          } catch (error) {
            console.error("Error testing payment API:", error);
          }
        } else {
          console.log("getClientPayments function not available");
        }
      } catch (error) {
        toast.error("Error fetching data");
      } finally {
        setIsLoading(false);
      }
    };

    if (!authLoading && user) {
      fetchData();
    }
  }, [
    // Reduce dependency array to only essential items
    authLoading, 
    user, 
    getClients, 
    getAgencies
    // Remove these from dependency array to prevent cascading effects
    // getClientHistory,
    // getAgencyReservations,
  ]);

  useEffect(() => {
    const loadAnalytics = async () => {
      if (activeTab === 'analysis') {
        try {
          const data = await getAnalytics();
          setAnalysisData(data);
        } catch (error) {
          toast.error('Error loading analytics');
        }
      }
    };

    loadAnalytics();
  }, [activeTab, getAnalytics]);

  useEffect(() => {
    // Clear filters when tab changes
    setSearchTerm("");
    setFilterStatus("");
  }, [activeTab]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-brand-500"></div>
      </div>
    );
  }
  if (!user) {
    return <div className="text-gray-900 dark:text-gray-100">Por favor, inicie sesión para acceder a esta sección.</div>;
  }

  // Funciones para Clientes (B2C)
  const handleAddClient = async (clientData: Omit<ClienteB2CExtended, "id" | "reservations">) => {
    try {
      const newClient = await createClient(clientData);
      // Nuevo cliente recién creado tendrá 0 reservas.
      const clientWithReservations = { ...newClient, reservations: 0 };
      setClients((prev) => [...prev, clientWithReservations]);
      return clientWithReservations; // Devolver el cliente creado
    } catch (error) {
      toast.error("Error adding client");
      throw error; // Re-lanzar el error para que el modal pueda manejarlo
    }
  };

  const handleEditClient = async (client: ClienteB2CExtended) => {
    try {
      const updated = await updateClient(client);
      setClients((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    } catch (error) {
      toast.error("Error editing client");
    }
  };

  const handleDeleteClient = async (clientId: number) => {
    try {
      const result = await deleteClient(clientId);
      setClients((prev) => prev.filter((c) => c.id !== clientId));
      
      // Show detailed success message with deleted entities count
      const { deletedData } = result;
      if (deletedData) {
        const totalDeleted = deletedData.reservations + deletedData.invoices + deletedData.income;
        if (totalDeleted > 0) {
          toast.success(
            `Cliente eliminado exitosamente junto con ${totalDeleted} elemento${totalDeleted !== 1 ? 's' : ''} relacionado${totalDeleted !== 1 ? 's' : ''} (${deletedData.reservations} reserva${deletedData.reservations !== 1 ? 's' : ''}, ${deletedData.invoices} factura${deletedData.invoices !== 1 ? 's' : ''}, ${deletedData.income} registro${deletedData.income !== 1 ? 's' : ''} de pago)`,
            { duration: 5000 }
          );
        } else {
          toast.success("Cliente eliminado exitosamente");
        }
      } else {
        toast.success("Cliente eliminado exitosamente");
      }
    } catch (error) {
      toast.error("Error al eliminar el cliente");
      console.error("Error deleting client:", error);
    }
  };

  // Funciones para Agencias (B2B)
  const handleAddAgency = async (agencyData: Omit<AgenciaB2B, "id" | "reservations">) => {
    try {
      const newAgency = await createAgency(agencyData);
      setAgencies((prev) => [...prev, { ...newAgency, reservations: 0 }]);
      return newAgency; // Retornar la nueva agencia creada
    } catch (error) {
      toast.error("Error adding agency");
      throw error;
    }
  };

  const handleEditAgency = async (agency: AgenciaB2B) => {
    try {
      const updated = await updateAgency(agency);
      setAgencies((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    } catch (error) {
      toast.error("Error editing agency");
    }
  };

  const handleDeleteAgency = async (agencyId: number) => {
    try {
      await deleteAgency(agencyId);
      setAgencies((prev) => prev.filter((a) => a.id !== agencyId));
    } catch (error) {
      toast.error("Error deleting agency");
    }
  };

  return (
    <div>
      <PageMeta
        title="Gestión de Clientes | Destiny Caribbean Tours"
        description="Administración completa de los clientes."
      />
      <PageBreadcrumb pageTitle="Clientes" />
      <div className="min-h-screen rounded-2xl border border-gray-200 bg-white px-2 py-3 dark:border-gray-700 dark:bg-gray-800 sm:px-3 lg:px-4">
        <div className="p-4 sm:p-6 lg:p-8">
          {/* Encabezado y Tabs */}
          <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-black dark:text-white">Gestión de Clientes</h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Administre clientes B2C, agencias B2B y vea análisis
              </p>
            </div>
            <div className="mt-4 md:mt-0">
              <div className="inline-flex items-center shadow-theme-xs">
                {["b2c", "b2b", "analysis"].map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab as "b2c" | "b2b" | "analysis")}
                    className={`${
                      activeTab === tab
                        ? "bg-brand-500 text-white hover:bg-brand-600"
                        : "text-brand-500 bg-transparent hover:bg-brand-500 hover:text-white"
                    } inline-flex items-center gap-2 px-4 py-3 text-sm font-medium ring-1 ring-inset ring-brand-500 transition first:rounded-l-lg last:rounded-r-lg`}
                  >
                    {tab === "b2c" ? "Clientes B2C" : tab === "b2b" ? "Agencias B2B" : "Análisis"}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div>
            {activeTab === "b2c" && (
              <ClientList
                clients={clients}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onAddClient={handleAddClient}
                onEditClient={handleEditClient}
                onDeleteClient={handleDeleteClient}
                getClientById={getClientById}
                getClientHistory={getClientHistory}
                getClientPayments={getClientPayments}
                filterStatus={filterStatus}
                onFilterChange={setFilterStatus}
              />
            )}
            {activeTab === "b2b" && (
              <AgencyList
                agencies={agencies}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onAddAgency={handleAddAgency}
                onEditAgency={handleEditAgency}
                onDeleteAgency={handleDeleteAgency}
                getAgencyById={getAgencyById}
                getAgencyReservations={getAgencyReservations}
                getAgencyPayments={getAgencyPayments}
                filterStatus={filterStatus}
                onFilterChange={setFilterStatus}
              />
            )}
            {activeTab === "analysis" && (
              <AnalisisClientes
                mostPaid={analysisData.mostPaid}
                leastPaid={analysisData.leastPaid}
                topAgencyMetric={analysisData.topAgencyMetric}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Clientes;